import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Avatar, Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ScrollableContainer from '../utility/ScrollableContainer';
import Header from '../Common/UI/Header';
import TextField from '../Common/UI/TextField';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { AppDispatch, StoreState } from '../../redux/Store';
import { useDispatch } from 'react-redux';
import {
    clearUserErrors,
    updateUser,
    updateUserAvatarAsync,
    updateUserNameAsync,
    updateUserPhoneAsync
} from '../../redux/user/Actions';

import ConfirmEmailPasswordDialog from './ConfirmEmailPassword';
import { UploadIcon } from '../UI/icons';

const useStyles = makeStyles((theme: Theme) => style(theme));

const defaultProps = {};

const initialDirtyState = {
    firstName: false,
    lastName: false,
    phone: false,
    email: false
};

const keysToCheck = ['firstName', 'lastName', 'email', 'phone'];
const inputFields = [
    { name: 'firstName', label: 'First Name' },
    { name: 'lastName', label: 'Last Name' },
    { name: 'email', label: 'Email' },
    { name: 'phone', label: 'Phone Number' }
];

function Profile() {
    const classes = useStyles();
    const dispatch = useDispatch<AppDispatch>();

    const user = useSelector((state: StoreState) => state.user);

    const [localUser, setLocalUser] = useState<any>(user);
    const [dirtyState, setDirtyState] = useState<{ [key: string]: boolean }>(initialDirtyState);
    const [isConfirmEmailPasswordDialogOpen, setIsConfirmEmailPasswordDialogOpen] = useState(false);
    function toggleConfirmEmailPasswordDialogOpen() {
        setIsConfirmEmailPasswordDialogOpen(p => !p);
    }
    const [isPasswordChange, setIsPasswordChange] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    function handleUploadAvatarClick() {
        fileInputRef?.current?.click();
    }
    function handleAvatarChange(event: ChangeEvent<HTMLInputElement>) {
        const file = event?.target?.files?.[0];
        if (file) {
            dispatch(updateUserAvatarAsync(file));
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }
    function handleRemoveAvatar() {
        dispatch(updateUserAvatarAsync(null));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    const isDirty = Object.values(dirtyState).some(value => value);

    function handleChange(target: HTMLInputElement) {
        setLocalUser((p: any) => ({ ...p, [target.name]: target.value }));
    }

    function handleSave() {
        if (dirtyState?.firstName || dirtyState?.lastName) dispatch(updateUserNameAsync(localUser));
        if (dirtyState?.phone) dispatch(updateUserPhoneAsync(localUser));
        if (dirtyState?.email) toggleConfirmEmailPasswordDialogOpen();
    }

    function handleCancel() {
        dispatch(updateUser(user));
        setLocalUser(user);
    }

    function handleChangePasswordClick() {
        setIsPasswordChange(true);
        toggleConfirmEmailPasswordDialogOpen();
    }

    function onCloseEmailPasswordDialod() {
        dispatch(clearUserErrors());
        toggleConfirmEmailPasswordDialogOpen();
    }

    useEffect(() => {
        keysToCheck.forEach(key => {
            setDirtyState(p => ({ ...p, [key]: localUser[key] !== user[key] }));
        });
    }, [localUser, user]);

    return (
        <div className={classes.root}>
            {isConfirmEmailPasswordDialogOpen && (
                <ConfirmEmailPasswordDialog
                    open={isConfirmEmailPasswordDialogOpen}
                    email={localUser?.email}
                    isPasswordChange={isPasswordChange}
                    onClose={onCloseEmailPasswordDialod}
                    setIsPasswordChange={setIsPasswordChange}
                />
            )}
            <Header>
                <Typography variant="body1" color="textPrimary">
                    Settings
                </Typography>
            </Header>
            <ScrollableContainer className={classes.page} grow>
                <div className={classes.container}>
                    <div className={classes.header}>
                        <Typography variant="body1" className={classes.headerContent}>
                            <PersonIcon fontSize="large" />
                            PROFILE
                        </Typography>
                    </div>
                    <Divider className={classes.divider} />
                    <div className={classes.body}>
                        <Grid container>
                            <Grid item container className={classes.gridRow}>
                                <Grid item xs={4}>
                                    <Typography variant="body2">Profile Picture</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Box display="flex">
                                        {user?.avatar ? (
                                            <img
                                                src={user?.avatar}
                                                alt="profile_avatar"
                                                className={classes.profilePicture}
                                            />
                                        ) : (
                                            <Avatar
                                                alt="profile_avatar"
                                                className={classNames(
                                                    classes.profilePicture,
                                                    classes.avatar
                                                )}>
                                                {user?.firstName?.charAt(0)}
                                            </Avatar>
                                        )}
                                        <Box marginLeft="10px" alignSelf="center">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                className={classes.hidden}
                                                onChange={handleAvatarChange}
                                            />
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                className={classes.uploadButton}
                                                onClick={handleUploadAvatarClick}>
                                                Upload
                                                <UploadIcon />
                                            </Button>
                                            <Button
                                                className={classes.removeProfilePictureButton}
                                                onClick={handleRemoveAvatar}>
                                                <DeleteOutlineIcon fontSize="small" />
                                                Remove profile picture
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                            {inputFields.map(inputField => (
                                <Grid
                                    key={inputField.name}
                                    item
                                    container
                                    className={classes.gridRow}>
                                    <Grid item xs={4}>
                                        <Typography variant="body2">{inputField.label}</Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextField
                                            fullWidth
                                            name={inputField.name}
                                            value={localUser[inputField.name]}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleChange(e.target)
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                            <Grid item container className={classes.gridRow}>
                                <Grid item xs={4}>
                                    <Typography variant="body2">Organisation</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="body2" className={classes.organisation}>
                                        {localUser?.organisationName}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item container className={classes.gridRow}>
                                <Grid item xs={4}>
                                    <Typography variant="body2">Password</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Button
                                        color="secondary"
                                        variant="outlined"
                                        className={classes.changePassword}
                                        onClick={handleChangePasswordClick}>
                                        Change Password
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                    <Divider className={classes.divider} />
                    <div className={classes.footer}>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.footerButton}
                            disabled={!isDirty}
                            onClick={handleSave}>
                            Save
                        </Button>
                        <Button
                            variant="outlined"
                            className={classNames(classes.footerButton, classes.cancel)}
                            onClick={handleCancel}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </ScrollableContainer>
        </div>
    );
}

Profile.defaultProps = defaultProps;

const style = (theme: Theme) => ({
    root: {
        backgroundColor: theme.palette.background.main,
        color: theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column'
    },
    page: {
        padding: 60,
        display: 'flex',
        justifyContent: 'center',
        '&> *:not(:first-child)': {
            marginTop: 30
        }
    },
    container: {
        width: 700,
        height: 'fit-content',
        background: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4
    },
    divider: {
        margin: '0px 24px'
    },
    header: {
        padding: '22px 56px',
        fontSize: 16
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 2
    },
    body: {
        padding: '20px 56px'
    },
    gridRow: {
        margin: '8px 0px',
        alignItems: 'center',
        minHeight: 40
    },
    profilePicture: {
        height: 72,
        width: 72,
        borderRadius: 36
    },
    uploadButton: {
        width: 154,
        height: 32
    },
    removeProfilePictureButton: {
        marginTop: 6,
        padding: 0,
        color: theme.palette.primary.light,
        fontSize: 12,
        textTransform: 'none',
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: '20px'
    },
    organisation: {
        fontWeight: 500,
        marginLeft: 16
    },
    changePassword: {
        padding: '12px 42px'
    },
    footer: {
        padding: '20px 38px',
        display: 'flex',
        gap: 20
    },
    footerButton: {
        height: 40,
        width: 302
    },
    cancel: {
        color: '#c4c4c4'
    },
    hidden: { display: 'none' },
    avatar: {
        borderRadius: '50%',
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.primary.light
    }
});

export default Profile;
